import { Box, Button, Skeleton, Typography } from '@mui/material';
import { MAX_SHOWN_ACTIVE } from './ranking';

// The outcome list while the event (and the flow of its outcomes) loads: the
// rows it will show, as placeholders. The list fills in one step once all of
// it is there, so no row changes shape after it appears.

// A placeholder around a stand-in is as tall as the stand-in.
const FIT = { display: 'flex' } as const;
const ROWS = Array.from({ length: MAX_SHOWN_ACTIVE }, (_, i) => i);
// Name widths that vary a little, so the rows do not read as a grid.
const NAME_WIDTHS = ['46%', '34%', '58%', '40%', '30%', '50%'];

function TablePlaceholder() {
    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 0.5,
                    height: 24,
                }}
            >
                <Skeleton width={90} height={18} />
                <Box sx={{ flex: 1 }} />
                <Skeleton variant="rounded" width={180} height={24} />
            </Box>
            <Box sx={{ px: 1.5, py: 0.5 }}>
                <Skeleton sx={FIT}>
                    <Typography
                        sx={{
                            fontSize: '10px',
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                        }}
                    >
                        OUTCOME
                    </Typography>
                </Skeleton>
            </Box>
            {ROWS.map(i => (
                <Box
                    key={i}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        px: 1.5,
                        py: 0.75,
                    }}
                >
                    <Skeleton variant="rounded" width={22} height={22} />
                    <Skeleton
                        height={16}
                        sx={{ width: NAME_WIDTHS[i % NAME_WIDTHS.length] }}
                    />
                    <Box sx={{ flex: 1 }} />
                    <Skeleton width={150} height={16} />
                    <Skeleton width={110} height={16} />
                </Box>
            ))}
            <Skeleton sx={{ ...FIT, ml: 0.5, mt: 0.5 }}>
                <Button
                    size="small"
                    tabIndex={-1}
                    sx={{ fontSize: '11px', textTransform: 'none' }}
                >
                    Show more outcomes
                </Button>
            </Skeleton>
        </Box>
    );
}

function CardsPlaceholder() {
    return (
        <Box sx={{ m: -1.5 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 16px 0 16px',
                    minHeight: 44,
                }}
            >
                <Skeleton width={120} height={22} />
                <Skeleton width={110} height={18} />
            </Box>
            <Box sx={{ padding: '4px 16px 12px' }}>
                <Skeleton variant="rounded" height={44} />
            </Box>
            {ROWS.map(i => (
                <Box
                    key={i}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '34px minmax(0, 1fr) auto',
                        columnGap: 1.5,
                        rowGap: 0.75,
                        alignItems: 'center',
                        padding: '12px 16px',
                        borderTop: '1px solid',
                        borderTopColor: 'divider',
                    }}
                >
                    <Skeleton
                        variant="circular"
                        width={34}
                        height={34}
                        sx={{ gridRow: 'span 2' }}
                    />
                    <Skeleton
                        height={20}
                        sx={{ width: NAME_WIDTHS[i % NAME_WIDTHS.length] }}
                    />
                    <Skeleton width={44} height={20} />
                    <Skeleton height={14} sx={{ gridColumn: '2 / 4' }} />
                    <Skeleton
                        height={20}
                        sx={{ gridColumn: '2 / 3', width: '60%' }}
                    />
                </Box>
            ))}
            <Box
                sx={{
                    minHeight: 49,
                    borderTop: '1px solid',
                    borderTopColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Skeleton width={150} height={18} />
            </Box>
        </Box>
    );
}

export function OutcomeListPlaceholder({ phone }: { phone: boolean }) {
    return (
        <Box data-testid="outcome-list-placeholder">
            {phone ? <CardsPlaceholder /> : <TablePlaceholder />}
        </Box>
    );
}
